/**
 * Award-data SSOT — Support at Home (SAH) program
 *
 * Aged Care Rules 2025 (F2026C00802, Comp. No. 10, 1 Aug 2026) Chapter 4 —
 * Support at Home program. COMPLIANCE-AUDIT-FIX 2026-08-19 (grok finding
 * 12): this citation was stale at F2025L01173 — SAH-RULES.md's own header
 * has read F2026C00802 since the compliance round; corrected here to match
 * (comment-only, no engine-output change).
 * Effective from 1 November 2025; replaces Home Care Packages program for
 * new entrants. "No worse off" grandfathering for clients on HCP at
 * 12 September 2024.
 *
 * NOTE: This is regulator/program data, NOT a Fair Work modern award. It
 * sits in `award-data/` alongside the FWC awards because all four data
 * surfaces (SCHADS award + SAH program + QI + AN-ACC + residential fees +
 * compliance penalties) share the same SSOT discipline + provenance
 * requirements per the verify-first runbook.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * INDEXATION CADENCE (binding — set calendar reminders)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   - 1 July annual indexation (budgets, AT-HM caps)
 *   - 1 October: classification, transitioned-HCP and pathway funding moved
 *     again in 2026 (schedule v3, published 22/9/26) — check every quarter
 *     start, not only 1 July
 *   - 20 March / 20 September pension rate changes (income-free areas,
 *     contribution caps — NOT 1 July)
 *
 * The constants below hold the CURRENT schedule only and feed display,
 * scenario and mismatch paths. Posted money and budget provisioning read the
 * date-effective tables (sah_program_rates, contribution_cap_index_periods)
 * and sah_lifetime_cap(). Every indexation must update the tables AND these
 * constants in one change set.
 */

import { DEFAULT_TZ, getMonthInTz, getYearInTz } from '../timezone';

// ═══════════════════════════════════════════════════════════════════════
// Annual + quarterly budgets by classification
// ═══════════════════════════════════════════════════════════════════════

/**
 * SAH annual budget amounts by classification level effective 1 October 2026.
 *
 * Source: Schedule of subsidies and supplements for aged care – Support at
 * Home, rates effective 1 October 2026 (version 3), Appendix
 * (doh-schedule-subsidies-supplements-sah-2026-10-01.pdf, page 5).
 * Verified: 2026-09-30.
 */
export const SAH_ANNUAL_BUDGETS: Record<number, number> = {
  1: 11_031.56,
  2: 16_483.55,
  3: 22_580.93,
  4: 30_528.15,
  5: 40_809.27,
  6: 49_461.91,
  7: 59_776.8,
  8: 80_294.0,
};

/**
 * Transitional HCP-equivalent budgets for clients moving from the old Home Care
 * Packages program into Support at Home. These are DIFFERENT from the standard
 * 1-8 SAH classifications above — a transitioned HCP client retains their
 * legacy package level until reassessed into a standard SAH classification.
 *
 * Source: Schedule of subsidies and supplements for aged care – Support at
 * Home, rates effective 1 October 2026 (version 3), Appendix
 * (doh-schedule-subsidies-supplements-sah-2026-10-01.pdf, page 5).
 * Verified: 2026-09-30.
 *
 * Apply via the `transitioned_hcp_level` column on the client row when present;
 * fall back to the standard SAH_ANNUAL_BUDGETS by classification otherwise.
 */
export const SAH_HCP_TRANSITIONED_BUDGETS: Record<
  1 | 2 | 3 | 4,
  { quarterly: number; annual: number }
> = {
  1: { quarterly: 2_823.55, annual: 11_294.22 },
  2: { quarterly: 4_965.14, annual: 19_860.56 },
  3: { quarterly: 10_808.3, annual: 43_233.21 },
  4: { quarterly: 16_385.99, annual: 65_543.98 },
};

/**
 * SAH quarterly budgets by classification level — Annual ÷ 4 (rounded to cents).
 * SAH budgets are paid in 4 equal quarterly installments.
 * Source: the published quarterly column of the same appendix
 * (doh-schedule-subsidies-supplements-sah-2026-10-01.pdf, page 5), effective
 * 1 October 2026. Verified: 2026-09-30.
 */
export const SAH_QUARTERLY_BUDGETS: Record<number, number> = {
  1: 2_757.89,
  2: 4_120.89,
  3: 5_645.23,
  4: 7_632.04,
  5: 10_202.32,
  6: 12_365.48,
  7: 14_944.2,
  8: 20_073.5,
};

// ═══════════════════════════════════════════════════════════════════════
// Budget resolution helpers
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// Dated budget schedules
// ═══════════════════════════════════════════════════════════════════════

export interface SahBudgetSchedule {
  /** First day the schedule applies, YYYY-MM-DD. */
  effectiveFrom: string;
  /** Last day the schedule applies (inclusive), or null while it is current. */
  effectiveTo: string | null;
  /** How the Department names it, e.g. "1 October 2026". */
  label: string;
  annual: Readonly<Record<number, number>>;
  quarterly: Readonly<Record<number, number>>;
  transitioned: Readonly<Record<1 | 2 | 3 | 4, { quarterly: number; annual: number }>>;
}

/**
 * Every Support at Home funding schedule the program has had, oldest first.
 * TypeScript mirror of the ONGOING_CLASSIFICATION and HCP_TRANSITION rows in
 * `sah_program_rates`; a budget for a past quarter must be compared with the
 * schedule in force at that quarter's start, not with today's.
 *
 * Sources: doh-sah-classifications-and-budgets-2025-11.pdf (1 November 2025);
 * Schedule of subsidies and supplements for aged care – Support at Home,
 * 1 July 2026 and 1 October 2026 editions, Appendix.
 * Parity with the migrations is asserted in __tests__/sah-budget-schedules.test.ts.
 */
export const SAH_BUDGET_SCHEDULES: readonly SahBudgetSchedule[] = [
  {
    effectiveFrom: '2025-11-01',
    effectiveTo: '2026-06-30',
    label: '1 November 2025',
    annual: {
      1: 10_731.0,
      2: 16_034.45,
      3: 21_965.7,
      4: 29_696.4,
      5: 39_697.4,
      6: 48_114.3,
      7: 58_148.15,
      8: 78_106.35,
    },
    quarterly: {
      1: 2_682.75,
      2: 4_008.61,
      3: 5_491.43,
      4: 7_424.1,
      5: 9_924.35,
      6: 12_028.58,
      7: 14_537.04,
      8: 19_526.59,
    },
    transitioned: {
      1: { quarterly: 2_746.63, annual: 10_986.5 },
      2: { quarterly: 4_829.86, annual: 19_319.45 },
      3: { quarterly: 10_513.83, annual: 42_055.3 },
      4: { quarterly: 15_939.55, annual: 63_758.2 },
    },
  },
  {
    effectiveFrom: '2026-07-01',
    effectiveTo: '2026-09-30',
    label: '1 July 2026',
    annual: {
      1: 11_010.01,
      2: 16_451.35,
      3: 22_536.81,
      4: 30_468.51,
      5: 40_729.53,
      6: 49_365.27,
      7: 59_660.0,
      8: 80_137.12,
    },
    quarterly: {
      1: 2_752.5,
      2: 4_112.84,
      3: 5_634.2,
      4: 7_617.13,
      5: 10_182.38,
      6: 12_341.32,
      7: 14_915.0,
      8: 20_034.28,
    },
    transitioned: {
      1: { quarterly: 2_818.04, annual: 11_272.15 },
      2: { quarterly: 4_955.44, annual: 19_821.76 },
      3: { quarterly: 10_787.18, annual: 43_148.74 },
      4: { quarterly: 16_353.98, annual: 65_415.91 },
    },
  },
  {
    effectiveFrom: '2026-10-01',
    effectiveTo: null,
    label: '1 October 2026',
    annual: SAH_ANNUAL_BUDGETS,
    quarterly: SAH_QUARTERLY_BUDGETS,
    transitioned: SAH_HCP_TRANSITIONED_BUDGETS,
  },
];

/**
 * The schedule in force on a day (YYYY-MM-DD). With no date, the current
 * schedule. A date before the first schedule resolves to the first one.
 */
export function sahBudgetScheduleFor(date?: string | null): SahBudgetSchedule {
  const current = SAH_BUDGET_SCHEDULES[SAH_BUDGET_SCHEDULES.length - 1] as SahBudgetSchedule;
  const day = date == null ? '' : String(date).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return current;
  const match = [...SAH_BUDGET_SCHEDULES].reverse().find((row) => row.effectiveFrom <= day);
  return match ?? (SAH_BUDGET_SCHEDULES[0] as SahBudgetSchedule);
}

/**
 * Resolved canonical SAH budget for a client.
 *
 * - `source: 'transitioned'` — the client is on a transitional HCP-equivalent
 *   budget (set via `transitioned_hcp_level` 1-4).
 * - `source: 'standard'` — the client is on a standard SAH classification 1-8.
 * - `source: 'unclassified'` — neither field set; returns null budgets.
 */
export interface SahBudgetResolution {
  annual: number | null;
  quarterly: number | null;
  source: 'transitioned' | 'standard' | 'unclassified';
  /** Human-readable label for UI display, e.g. "Transitioned HCP Level 2" or "SAH Class 4". */
  label: string;
}

/**
 * Resolve the canonical SAH budget for a client.
 *
 * Precedence rule (from doh-sah-classifications-and-budgets-2025-11.pdf page 2):
 *   1. If `transitionedHcpLevel` is set (1-4), the client retains their HCP-equivalent
 *      budget until reassessed.
 *   2. Otherwise if `classificationLevel` is set (1-8), use the standard SAH budget.
 *   3. Otherwise the client is unclassified and budgets are null.
 *
 * Use this helper EVERYWHERE budgets are computed or displayed for a SAH client.
 * Do not derive budgets directly from SAH_ANNUAL_BUDGETS without consulting
 * `transitionedHcpLevel` first.
 */
export function getSahBudgetForClient(input: {
  classificationLevel?: number | null;
  transitionedHcpLevel?: number | null;
  /**
   * Day whose schedule applies (YYYY-MM-DD), normally the quarter start of the
   * budget being shown. Omit for the current schedule.
   */
  asOfDate?: string | null;
}): SahBudgetResolution {
  const { classificationLevel, transitionedHcpLevel } = input;
  const schedule = sahBudgetScheduleFor(input.asOfDate);

  if (transitionedHcpLevel != null && transitionedHcpLevel >= 1 && transitionedHcpLevel <= 4) {
    const tier = transitionedHcpLevel as 1 | 2 | 3 | 4;
    const entry = schedule.transitioned[tier];
    return {
      annual: entry.annual,
      quarterly: entry.quarterly,
      source: 'transitioned',
      label: `Transitioned HCP Level ${tier}`,
    };
  }

  if (classificationLevel != null && classificationLevel >= 1 && classificationLevel <= 8) {
    return {
      annual: schedule.annual[classificationLevel] ?? null,
      quarterly: schedule.quarterly[classificationLevel] ?? null,
      source: 'standard',
      label: `SAH Class ${classificationLevel}`,
    };
  }

  return {
    annual: null,
    quarterly: null,
    source: 'unclassified',
    label: 'Unclassified',
  };
}

export interface SahBudgetMismatch {
  /** Stored value as it appears in the DB. */
  stored: number;
  /** Canonical value from getSahBudgetForClient. */
  canonical: number;
  /** stored − canonical (positive = stored is higher than canonical). */
  delta: number;
  /** Which figure was compared. */
  field: 'annual' | 'quarterly';
  /** Context label, e.g. "SAH Class 4" or "Transitioned HCP Level 2". */
  resolutionLabel: string;
  /** The schedule the canonical figure comes from, e.g. "1 October 2026". */
  canonicalScheduleLabel: string;
  /** The schedule the stored figure matches, or null when it matches none. */
  storedScheduleLabel: string | null;
  /** Human-readable summary suitable for a warning toast or banner. */
  message: string;
}

// ═══════════════════════════════════════════════════════════════════════
// Quarter resolution helpers
// ═══════════════════════════════════════════════════════════════════════

/**
 * Resolve the SAH quarterly billing period for a given date.
 *
 * SAH quarters are aligned to calendar quarters in Australia/Sydney time:
 *   Q1: 1 Jan – 31 Mar
 *   Q2: 1 Apr – 30 Jun
 *   Q3: 1 Jul – 30 Sep
 *   Q4: 1 Oct – 31 Dec
 *
 * Per doh-sah-classifications-and-budgets-2025-11.pdf page 2: budgets are
 * provided on a quarterly basis. Calendar quarters are the common-sense
 * default and what Services Australia uses for SAH claims.
 */
export interface SahQuarter {
  /** ISO YYYY-MM-DD — first day of the quarter (inclusive) */
  start: string;
  /** ISO YYYY-MM-DD — last day of the quarter (inclusive) */
  end: string;
  /** Calendar year */
  year: number;
  /** Quarter number 1-4 */
  quarter: 1 | 2 | 3 | 4;
  /** Display label e.g. "Q2 2026 (Apr–Jun 2026)" */
  label: string;
}

export function getCurrentSahQuarter(
  now: Date = new Date(),
  timezone: string = DEFAULT_TZ,
): SahQuarter {
  return getSahQuarterForDate(now, timezone);
}

export function getSahQuarterForDate(date: Date, timezone: string = DEFAULT_TZ): SahQuarter {
  const year = getYearInTz(date, timezone);
  const month = getMonthInTz(date, timezone); // 0-11
  const quarter = (Math.floor(month / 3) + 1) as 1 | 2 | 3 | 4;
  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;
  const endDay = new Date(Date.UTC(year, endMonth + 1, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  const start = `${year}-${pad(startMonth + 1)}-01`;
  const end = `${year}-${pad(endMonth + 1)}-${pad(endDay)}`;
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const label = `Q${quarter} ${year} (${monthNames[startMonth]}–${monthNames[endMonth]} ${year})`;
  return { start, end, year, quarter, label };
}

/** Resolve the previous quarter for rollover purposes. */
export function getPreviousSahQuarter(
  date: Date = new Date(),
  timezone: string = DEFAULT_TZ,
): SahQuarter {
  const current = getSahQuarterForDate(date, timezone);
  const parts = current.start.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const startOfCurrent = new Date(Date.UTC(y, m - 1, d));
  startOfCurrent.setUTCDate(startOfCurrent.getUTCDate() - 1);
  return getSahQuarterForDate(startOfCurrent, timezone);
}

// ═══════════════════════════════════════════════════════════════════════
// Carry-over + budget split helpers
// ═══════════════════════════════════════════════════════════════════════

/**
 * Calculate the carry-over amount allowed at quarter end per the SAH rules.
 *
 * Aged Care Rules 2025 s 193-5; SAH Program Manual 9.7.1 (as revised Jul
 * 2026), s 194-5 vs s 204-5 two-stream split: carry-over is capped at the
 * HIGHER of $1,000 (bare literal, unindexed) or 10% of the NET quarterly
 * base — the base individual amount (post care-management split) PLUS this
 * quarter's primary person-centred supplements. `quarterlyAllocated` here
 * MUST be that net base, never the gross classification budget (which
 * includes the ~10% pooled care-management reserve) — using gross
 * over-states the cap for classification >= 6, where care management is a
 * larger absolute slice. Callers: pass `splitSahBudget(gross).serviceBudget
 * + primarySupplements`, not the raw classification budget.
 */
export function calculateSahCarryOver(input: { unspent: number; quarterlyAllocated: number }): {
  carryOver: number;
  forfeited: number;
} {
  const { unspent, quarterlyAllocated } = input;
  if (unspent <= 0) return { carryOver: 0, forfeited: 0 };

  const cap = Math.max(
    SAH_CARRY_OVER.minimumCarryOver,
    quarterlyAllocated * SAH_CARRY_OVER.carryOverPercentage,
  );
  const carryOver = Math.min(unspent, cap);
  const forfeited = Math.max(0, unspent - cap);
  return {
    carryOver: Math.round(carryOver * 100) / 100,
    forfeited: Math.round(forfeited * 100) / 100,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// HCP unspent funds — held-party breakdown + draw-down
// ═══════════════════════════════════════════════════════════════════════
//
// Grandfathered HCP transitioners retain their unspent HCP funds for use in SAH.
// `sah_client_budgets.hcp_legacy_carry_forward` is the running COMMONWEALTH
// service-funding projection (provider-held + Services-Australia-held). The
// care-recipient portion is participant-owned money in its own account/ledger;
// it is shown on statements but is never service funding.
//
// DoHDA labels (statement template) — what the three map to:
//   providerHeld     → "Individual's unspent Commonwealth portion"
//                      (provider-held slice of the Commonwealth subsidy, pre-1 Sep 2021)
//   notionalAccount  → "Balance of the individual's notional home care account"
//                      (held by Services Australia — NOT "Medicare")
//   clientContributions → "Individual's unspent care recipient portion"
//                      (the participant's own unspent HCP fees, provider-held)
//
// Draw-down order: Aged Care Act 2024 s 251B and DoHDA operating guidance use
// provider-held Commonwealth before the government-held account. The
// care-recipient portion is excluded from that order. Under Rules 273A-20 it may
// reduce only actual s 273 participant contributions after an evidence-bound
// RETAIN election; that separate ledger operation is not modelled as service
// drawdown here.

export interface HcpUnspentSplit {
  /** Care-recipient portion (Type A) — the participant's own unspent HCP fees. */
  clientContributions: number;
  /** Provider-held slice of the Commonwealth subsidy (B1). */
  providerHeld: number;
  /** Services-Australia-held notional home care account (B2). */
  notionalAccount: number;
}

export interface HcpUnspentDrawdownResult {
  /** Remaining balance per component after the draw. */
  closing: HcpUnspentSplit;
  /** How much each component was depleted by. */
  consumedByComponent: HcpUnspentSplit;
  /** Actual amount drawn (≤ amountConsumed; capped at the opening total). */
  totalConsumed: number;
  /** Amount that could NOT be drawn because the opening pool was exhausted. */
  unmetConsumption: number;
  /** Compatibility field. Always false: service drawdown never touches participant money. */
  clientPortionDepleted: boolean;
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Deplete Commonwealth HCP-unspent money by `amountConsumed`, in the statutory
 * claim order (provider-held Commonwealth → notional account). The participant's
 * care-recipient portion is deliberately excluded. Pure + deterministic.
 *
 * Reconciliation invariant: providerHeld + notionalAccount should equal the
 * client's Commonwealth-only `hcp_legacy_carry_forward` projection.
 */
export function depleteHcpUnspent(
  opening: HcpUnspentSplit,
  amountConsumed: number,
): HcpUnspentDrawdownResult {
  let remaining = Math.max(0, r2(amountConsumed));

  const drawFrom = (available: number): number => {
    const avail = Math.max(0, available);
    const drawn = r2(Math.min(avail, remaining));
    remaining = r2(remaining - drawn);
    return drawn;
  };

  // Confirmed Commonwealth order only (B1 → B2). Participant money is not a
  // fallback even when Commonwealth funds are exhausted.
  const fromProvider = drawFrom(opening.providerHeld);
  const fromNotional = drawFrom(opening.notionalAccount);

  return {
    closing: {
      providerHeld: r2(Math.max(0, opening.providerHeld) - fromProvider),
      notionalAccount: r2(Math.max(0, opening.notionalAccount) - fromNotional),
      clientContributions: r2(Math.max(0, opening.clientContributions)),
    },
    consumedByComponent: {
      clientContributions: 0,
      providerHeld: fromProvider,
      notionalAccount: fromNotional,
    },
    totalConsumed: r2(fromProvider + fromNotional),
    unmetConsumption: remaining,
    clientPortionDepleted: false,
  };
}

/** Sum the three held-party components (the reconciliation total). */
export function hcpUnspentTotal(split: HcpUnspentSplit): number {
  return r2(
    Math.max(0, split.clientContributions) +
      Math.max(0, split.providerHeld) +
      Math.max(0, split.notionalAccount),
  );
}

export interface StatementHcpUnspent {
  /** True only when the held-party split is populated AND reconciles to the running
   *  total — drives whether the statement shows the 3-line breakdown or just the total. */
  hasBreakdown: boolean;
  /** Month-opening / month-closing held-party split (null when !hasBreakdown). */
  opening: (HcpUnspentSplit & { total: number }) | null;
  closing: (HcpUnspentSplit & { total: number }) | null;
  /** Month-opening / month-closing HCP-unspent total (always set). */
  openingTotal: number;
  closingTotal: number;
  /** Evidence-bound participant-money status shown separately from Commonwealth funding. */
  participantMoney: {
    disposition: 'UNKNOWN' | 'RETURN' | 'RETAIN_FOR_S273_CONTRIBUTIONS' | null;
    status:
      | 'QUARANTINED'
      | 'PENDING_VERIFICATION'
      | 'VERIFIED_RETAIN'
      | 'RETURN_DUE'
      | 'RETURNED'
      | 'CLOSED'
      | null;
    openingBalance: number;
    closingBalance: number;
    appliedToContributionsThisPeriod: number;
    financiallyQuarantined: boolean;
  };
}

/**
 * Derive a monthly statement's HCP-unspent opening/closing presentation from the
 * quarter-opening Commonwealth split + its running total + this quarter's spend.
 * Participant-money opening/closing values come only from its append-only ledger;
 * when absent, the captured participant opening remains unchanged and quarantined.
 *
 * Pure. Mirrors the engine's legacy draw-down basis exactly (legacy is consumed only
 * AFTER standard quarterly funds = allocated + carry-over-in are exhausted), so the
 * derived figures reconcile to `hcp_legacy_carry_forward`. The 3-way is a BREAKDOWN of
 * that already-counted total — it is NEVER added to the available balance again.
 *
 * Fail-soft presentation: when the 3 columns are unpopulated (sum 0 while the legacy
 * total is > 0) the split does not reconcile, so `hasBreakdown` is false and the caller
 * shows the total alone — never a mis-stated or guessed split.
 */
export function deriveStatementHcpUnspent(input: {
  quarterOpeningSplit: HcpUnspentSplit;
  /** Commonwealth-only provider-held + Services-Australia-held projection. */
  legacyTotal: number;
  standardQuarterlyAvailable: number;
  priorMonthsSpending: number;
  thisMonthCost: number;
  participantMoney?: {
    openingBalance: number;
    closingBalance: number;
    appliedToContributionsThisPeriod?: number;
    disposition?: 'UNKNOWN' | 'RETURN' | 'RETAIN_FOR_S273_CONTRIBUTIONS' | null;
    status?:
      | 'QUARANTINED'
      | 'PENDING_VERIFICATION'
      | 'VERIFIED_RETAIN'
      | 'RETURN_DUE'
      | 'RETURNED'
      | 'CLOSED'
      | null;
  };
}): StatementHcpUnspent {
  const legacyTotal = Math.max(0, r2(input.legacyTotal));
  const commonwealthSplitTotal = r2(
    Math.max(0, input.quarterOpeningSplit.providerHeld) +
      Math.max(0, input.quarterOpeningSplit.notionalAccount),
  );
  const participantOpening = Math.max(
    0,
    r2(input.participantMoney?.openingBalance ?? input.quarterOpeningSplit.clientContributions),
  );
  const participantClosing = Math.max(
    0,
    r2(input.participantMoney?.closingBalance ?? participantOpening),
  );
  const participantDisposition = input.participantMoney?.disposition ?? 'UNKNOWN';
  const participantStatus = input.participantMoney?.status ?? 'QUARANTINED';
  const hasBreakdown =
    (legacyTotal > 0 || participantOpening > 0 || participantClosing > 0) &&
    // eslint-disable-next-line statura/no-floating-point-money -- reconciliation tolerance: both operands already round2'd; $0.01 comparison, not a stored money value
    Math.abs(commonwealthSplitTotal - legacyTotal) <= 0.01;
  const participantMoney: StatementHcpUnspent['participantMoney'] = {
    disposition: participantDisposition,
    status: participantStatus,
    openingBalance: participantOpening,
    closingBalance: participantClosing,
    appliedToContributionsThisPeriod: Math.max(
      0,
      r2(input.participantMoney?.appliedToContributionsThisPeriod ?? 0),
    ),
    financiallyQuarantined:
      participantOpening > 0 &&
      !['VERIFIED_RETAIN', 'RETURN_DUE', 'RETURNED', 'CLOSED'].includes(participantStatus ?? ''),
  };

  const consumedToPriorMonthEnd = Math.max(
    0,
    r2(input.priorMonthsSpending - input.standardQuarterlyAvailable),
  );
  const consumedToMonthEnd = Math.max(
    0,
    r2(input.priorMonthsSpending + input.thisMonthCost - input.standardQuarterlyAvailable),
  );

  if (!hasBreakdown) {
    const openingCommonwealth = r2(Math.max(0, legacyTotal - consumedToPriorMonthEnd));
    const closingCommonwealth = r2(Math.max(0, legacyTotal - consumedToMonthEnd));
    return {
      hasBreakdown: false,
      opening: null,
      closing: null,
      openingTotal: r2(openingCommonwealth + participantOpening),
      closingTotal: r2(closingCommonwealth + participantClosing),
      participantMoney,
    };
  }

  const openingSplit = depleteHcpUnspent(
    input.quarterOpeningSplit,
    consumedToPriorMonthEnd,
  ).closing;
  const closingSplit = depleteHcpUnspent(input.quarterOpeningSplit, consumedToMonthEnd).closing;
  openingSplit.clientContributions = participantOpening;
  closingSplit.clientContributions = participantClosing;
  const openingTotal = hcpUnspentTotal(openingSplit);
  const closingTotal = hcpUnspentTotal(closingSplit);
  return {
    hasBreakdown: true,
    opening: { ...openingSplit, total: openingTotal },
    closing: { ...closingSplit, total: closingTotal },
    openingTotal,
    closingTotal,
    participantMoney,
  };
}

/**
 * Split a quarterly budget into the service portion and the care management
 * portion. Per doh-schedule-subsidies-supplements-sah-2025-10.pdf page 1
 * footnote and the Aged Care Rules 2025 Ch 4 SAH framework: a flat 10% of
 * the budget is reserved for care management.
 */
export function splitSahBudget(quarterlyBudget: number): {
  serviceBudget: number;
  careManagementBudget: number;
} {
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const careManagementBudget = Math.round(quarterlyBudget * SAH_CARE_MANAGEMENT_RATE * 100) / 100;
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const serviceBudget = Math.round((quarterlyBudget - careManagementBudget) * 100) / 100;
  return { serviceBudget, careManagementBudget };
}

export function getStoredBudgetMismatch(input: {
  storedAnnual?: number | null;
  storedQuarterly?: number | null;
  classificationLevel?: number | null;
  transitionedHcpLevel?: number | null;
  /**
   * Day whose schedule the stored figure should match (YYYY-MM-DD), normally the
   * stored budget's quarter start. Omit to compare with the current schedule.
   */
  asOfDate?: string | null;
}): SahBudgetMismatch | null {
  const schedule = sahBudgetScheduleFor(input.asOfDate);
  const resolution = getSahBudgetForClient({
    classificationLevel: input.classificationLevel,
    transitionedHcpLevel: input.transitionedHcpLevel,
    asOfDate: input.asOfDate,
  });
  const storedScheduleLabel = (field: 'annual' | 'quarterly', stored: number): string | null =>
    SAH_BUDGET_SCHEDULES.find((row) => {
      const canonical = getSahBudgetForClient({
        classificationLevel: input.classificationLevel,
        transitionedHcpLevel: input.transitionedHcpLevel,
        asOfDate: row.effectiveFrom,
      })[field];
      return canonical != null && Math.abs(stored - canonical) <= 0.01;
    })?.label ?? null;

  if (resolution.source === 'unclassified') return null;
  if (resolution.annual === null || resolution.quarterly === null) return null;

  if (input.storedAnnual !== undefined && input.storedAnnual !== null) {
    const delta = Math.round((input.storedAnnual - resolution.annual) * 100) / 100;
    if (Math.abs(delta) > 0.01) {
      const sign = delta > 0 ? '+' : '';
      return {
        stored: input.storedAnnual,
        canonical: resolution.annual,
        delta,
        field: 'annual',
        resolutionLabel: resolution.label,
        canonicalScheduleLabel: schedule.label,
        storedScheduleLabel: storedScheduleLabel('annual', input.storedAnnual),
        message: `Stored annual budget $${input.storedAnnual.toLocaleString('en-AU', { minimumFractionDigits: 2 })} does not match the canonical ${resolution.label} budget of $${resolution.annual.toLocaleString('en-AU', { minimumFractionDigits: 2 })} (${sign}$${delta.toFixed(2)}).`,
      };
    }
  }

  if (input.storedQuarterly !== undefined && input.storedQuarterly !== null) {
    const delta = Math.round((input.storedQuarterly - resolution.quarterly) * 100) / 100;
    if (Math.abs(delta) > 0.01) {
      const sign = delta > 0 ? '+' : '';
      return {
        stored: input.storedQuarterly,
        canonical: resolution.quarterly,
        delta,
        field: 'quarterly',
        resolutionLabel: resolution.label,
        canonicalScheduleLabel: schedule.label,
        storedScheduleLabel: storedScheduleLabel('quarterly', input.storedQuarterly),
        message: `Stored quarterly budget $${input.storedQuarterly.toLocaleString('en-AU', { minimumFractionDigits: 2 })} does not match the canonical ${resolution.label} quarterly budget of $${resolution.quarterly.toLocaleString('en-AU', { minimumFractionDigits: 2 })} (${sign}$${delta.toFixed(2)}).`,
      };
    }
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════
// Contribution rates + caps + means tiers
// ═══════════════════════════════════════════════════════════════════════

/**
 * SAH does NOT have a basic daily fee — contributions are per-service.
 * The 17.5% rate below is the Everyday Living contribution rate for full pensioners
 * under the STANDARD (non-grandfathered) framework.
 *
 * Source: services-australia-age-pension-rates-mar-2026.pdf — Single maximum basic
 * rate (Normal Rate) = $1,100.30/fortnight = $78.59/day.
 * Verified: 2026-04-08.
 *
 * IMPORTANT: Under the SAH "no worse off" rules, grandfathered Full Pensioners
 * actually pay 0% (not 17.5%) on Everyday Living per the contribution table in
 * doh-sah-participant-contributions-2025-12.pdf page 3. The
 * `grandfatheredBasicDailyFee` constant below is RETAINED for HCP-era backward
 * compatibility but should not be used to compute new SAH charges — use
 * `SAH_GRANDFATHERED_FIXED_RATES` instead.
 */
export const SAH_CONTRIBUTION_RATES = {
  everydayLivingFullPensionerRate: 0.175,
  // Single maximum basic rate of Age Pension from 20 September 2026:
  // $1,135.40 a fortnight / 14 = $81.10 a day (Services Australia, "How much
  // Age Pension you can get", page updated 20 September 2026; pinned as
  // docs/regulatory-sources/services-australia-age-pension-rates-20-sep-2026.html).
  // Verified 2026-10-01. Indexed 20 March and 20 September.
  singleBasicAgePensionDaily: 81.1,
  /**
   * @deprecated Under SAH "no worse off" rules, grandfathered Full Pensioners
   * pay 0% (not 17.5%) on Everyday Living. Use `SAH_GRANDFATHERED_FIXED_RATES`
   * for new SAH calculations.
   */
  grandfatheredBasicDailyFee: 14.19, // 17.5% of $81.10, from 20 September 2026 (was $13.75)
} as const;

/**
 * Support at Home means-test thresholds for Independence and Everyday Living
 * contributions, from 20 September 2026 (Department of Health, Disability and
 * Ageing, Schedule of contributions for Support at Home services from 20
 * September 2026, page 1; docs/regulatory-sources/doh-sah-schedule-of-contributions-20-sep-2026.pdf).
 * Verified 2026-10-01. Reviewed in March, July and September each year.
 *
 * Reference data only: Services Australia performs the assessment and issues
 * the participant's rates. Nothing in the platform derives a rate from these.
 *
 * Replaces `SAH_INCOME_FREE_AREAS` ($33,500 / $52,200), which matched no
 * published Support at Home figure and had no consumer.
 */
export const SAH_MEANS_TEST_THRESHOLDS = {
  effectiveFrom: '2026-09-20',
  nonPensionIncome: {
    single: { minimum: 5_876, maximum: 105_048 },
    couple: { minimum: 10_296, maximum: 168_076 },
    coupleSeparatedByIllness: { minimum: 10_296, maximum: 210_096 },
  },
  assets: {
    singleHomeowner: { minimum: 333_000, maximum: 968_717.95 },
    singleNonHomeowner: { minimum: 600_000, maximum: 1_235_717.95 },
    coupleHomeowner: { minimum: 499_000, maximum: 1_510_410.26 },
    coupleNonHomeowner: { minimum: 766_000, maximum: 1_777_410.26 },
    coupleSeparatedByIllnessHomeowner: { minimum: 499_000, maximum: 1_779_769.23 },
    coupleSeparatedByIllnessNonHomeowner: { minimum: 766_000, maximum: 2_046_769.23 },
  },
} as const;

/**
 * SAH contribution caps — LIFETIME ONLY (SAH has NO annual cap).
 *
 * IMPORTANT: The SAH lifetime cap is SHARED with the residential NCCC lifetime cap.
 * If a participant moves from SAH to residential care, prior SAH contributions
 * count against the NCCC lifetime cap. Cross-module data sharing required.
 *
 * Source: Schedule of contributions for Support at Home services from
 *         20 September 2026 (doh-sah-schedule-of-contributions-20-sep-2026.pdf,
 *         pages 1-2). Verified: 2026-09-30.
 * INDEXATION: 20 March and 20 September each year (NOT 1 July like budgets).
 * SQL mirror: sah_lifetime_cap(boolean,date) and contribution_cap_index_periods —
 * update all three in one change set.
 */
export const SAH_CONTRIBUTION_CAPS = {
  lifetimeCapNewEntrant: 140_652.8, // 20 September 2026 indexed (was $137,917.01 from 20 March 2026)
  lifetimeCapGrandfathered: 88_934.57, // 20 September 2026 indexed (no worse off; was $86,185.23)
} as const;

export interface SahContributionCapPeriod {
  /** First day the caps apply (YYYY-MM-DD). */
  effectiveFrom: string;
  lifetimeCapNewEntrant: number;
  lifetimeCapGrandfathered: number;
}

/**
 * Lifetime caps by indexation period, oldest first. Mirrors
 * `contribution_cap_index_periods`; the last row equals `SAH_CONTRIBUTION_CAPS`.
 */
export const SAH_CONTRIBUTION_CAP_PERIODS: readonly SahContributionCapPeriod[] = [
  {
    effectiveFrom: '2025-11-01',
    lifetimeCapNewEntrant: 135_318.69,
    lifetimeCapGrandfathered: 84_571.66,
  },
  {
    effectiveFrom: '2026-03-20',
    lifetimeCapNewEntrant: 137_917.01,
    lifetimeCapGrandfathered: 86_185.23,
  },
  {
    effectiveFrom: '2026-09-20',
    lifetimeCapNewEntrant: SAH_CONTRIBUTION_CAPS.lifetimeCapNewEntrant,
    lifetimeCapGrandfathered: SAH_CONTRIBUTION_CAPS.lifetimeCapGrandfathered,
  },
];

/**
 * The lifetime caps in force on a day (YYYY-MM-DD). With no date, the current
 * caps. An explicit invalid or uncovered date fails closed, like the SQL
 * sah_lifetime_cap(boolean,date) overload. The undated compatibility call
 * continues to return the current constants.
 */
export function sahContributionCapsFor(date?: string | null): SahContributionCapPeriod {
  const current = SAH_CONTRIBUTION_CAP_PERIODS[
    SAH_CONTRIBUTION_CAP_PERIODS.length - 1
  ] as SahContributionCapPeriod;
  if (date == null) return current;
  const day = String(date).slice(0, 10);
  const parsed = new Date(`${day}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(day) ||
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString() !== `${day}T00:00:00.000Z`
  ) {
    throw new RangeError(`No SAH contribution cap period covers ${date}`);
  }
  const match = [...SAH_CONTRIBUTION_CAP_PERIODS].reverse().find((row) => row.effectiveFrom <= day);
  if (!match) throw new RangeError(`No SAH contribution cap period covers ${date}`);
  return match;
}

/** @deprecated typo — use lifetimeCapGrandfathered. Kept for one release for backward compat. */
export const SAH_CONTRIBUTION_CAPS_LEGACY = {
  lifetimeCapGrantfathered: 88_934.57,
} as const;

/**
 * SAH per-service contribution rates by means-testing group.
 *
 * 4 means-test groups (NOT 3):
 * - FULL_PENSIONER: fixed minimum rates
 * - PART_PENSIONER: SLIDING SCALE — individually assessed by Services Australia
 * - CSHC_HOLDER: SLIDING SCALE — same path as part pensioner, separate enum value
 * - SELF_FUNDED_NO_CSHC: fixed maximum rates
 *
 * CRITICAL: PART_PENSIONER and CSHC_HOLDER tiers have NO default rates. They
 * MUST receive an `individualAssessedRate` from the Services Australia
 * assessment letter. Applying midpoint defaults would overcharge clients,
 * triggering pecuniary penalties under Aged Care Act 2024 Ch 4 ss 400-430.
 */
export type SahMeansTier =
  | 'FULL_PENSIONER'
  | 'PART_PENSIONER'
  | 'CSHC_HOLDER'
  | 'SELF_FUNDED_NO_CSHC';
export type SahServiceCategory = 'CLINICAL' | 'INDEPENDENCE' | 'EVERYDAY_LIVING';

/**
 * Fixed contribution rates — only for FULL_PENSIONER and SELF_FUNDED_NO_CSHC tiers.
 * For PART_PENSIONER and CSHC_HOLDER, the rate is per-client (Services Australia
 * assessment letter) and MUST be passed explicitly to the calculator.
 */
export const SAH_FIXED_CONTRIBUTION_RATES: Record<
  'FULL_PENSIONER' | 'SELF_FUNDED_NO_CSHC',
  Record<SahServiceCategory, number>
> = {
  FULL_PENSIONER: {
    CLINICAL: 0,
    INDEPENDENCE: 0.05,
    EVERYDAY_LIVING: 0.175,
  },
  SELF_FUNDED_NO_CSHC: {
    CLINICAL: 0,
    INDEPENDENCE: 0.5,
    EVERYDAY_LIVING: 0.8,
  },
};

/** Sliding-scale tiers — these REQUIRE individualAssessedRate; do not provide defaults. */
export const SAH_SLIDING_SCALE_TIERS = ['PART_PENSIONER', 'CSHC_HOLDER'] as const;

/** Sliding-scale ranges for STANDARD (non-grandfathered) clients.
 * For UI display only — NEVER apply midpoints to billing. */
export const SAH_SLIDING_SCALE_RANGES = {
  INDEPENDENCE: { min: 0.05, max: 0.5 },
  EVERYDAY_LIVING: { min: 0.175, max: 0.8 },
} as const;

/**
 * SAH "no worse off" grandfathered contribution rates.
 *
 * Applies to clients who were receiving a Home Care Package, or had been approved
 * for one, on 12 September 2024. These clients are guaranteed to pay the same OR
 * LESS under SAH than they did under the old HCP program.
 *
 * Source: doh-sah-participant-contributions-2025-12.pdf page 3.
 * Verified: 2026-04-08.
 */
export const SAH_GRANDFATHERED_FIXED_RATES: Record<
  'FULL_PENSIONER' | 'SELF_FUNDED_NO_CSHC',
  Record<SahServiceCategory, number>
> = {
  FULL_PENSIONER: {
    CLINICAL: 0,
    INDEPENDENCE: 0,
    EVERYDAY_LIVING: 0,
  },
  SELF_FUNDED_NO_CSHC: {
    CLINICAL: 0,
    INDEPENDENCE: 0.25,
    EVERYDAY_LIVING: 0.25,
  },
};

/**
 * SAH "no worse off" sliding-scale ranges for grandfathered PART_PENSIONER and
 * CSHC_HOLDER clients.
 *
 * Source: doh-sah-participant-contributions-2025-12.pdf page 3.
 * Verified: 2026-04-08.
 */
export const SAH_GRANDFATHERED_SLIDING_RANGES = {
  INDEPENDENCE: { min: 0, max: 0.25 },
  EVERYDAY_LIVING: { min: 0, max: 0.25 },
} as const;

/**
 * @deprecated DO NOT USE for new code. Use `SAH_FIXED_CONTRIBUTION_RATES` for
 * fixed tiers and pass `individualAssessedRate` for sliding-scale tiers.
 * The midpoint defaults below are UNSAFE — they overcharge sliding-scale clients.
 */
export const SAH_TIER_CONTRIBUTION_RATES: Record<string, Record<string, number>> = {
  FULL_PENSIONER: SAH_FIXED_CONTRIBUTION_RATES.FULL_PENSIONER,
  SELF_FUNDED_NO_CSHC: SAH_FIXED_CONTRIBUTION_RATES.SELF_FUNDED_NO_CSHC,
};

/** SAH carry-over rules */
export const SAH_CARRY_OVER = {
  minimumCarryOver: 1000, // $1,000 minimum
  carryOverPercentage: 0.1, // or 10% of quarterly budget, whichever is greater
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Assistive Technology — Home Modifications (AT-HM)
// ═══════════════════════════════════════════════════════════════════════

/**
 * AT-HM (Assistive Technology — Home Modifications) budget caps per 12-month assessment period.
 *
 * Source: doh-schedule-subsidies-supplements-sah-2025-10.pdf page 2
 * "Assistive Technology and Home Modifications (AT-HM) Scheme" tables,
 * re-verified against the 2026-07-01 indexed schedule
 * (schedule-of-subsidies-and-supplements-for-support-at-home.pdf) — this
 * constant was still pinned to the superseded 2025-11 figures until the
 * P5b defect-1 fix (2026-08-20), matching sah_program_rates AT_LOW/MED/HIGH
 * and HM_LOW/MED/HIGH allocation_cap.
 *
 * Two operational nuances NOT captured by the base values:
 * 1. The AT (Assistive Technology) High tier is "$15,390+" — participants with
 *    AT costs above $15,390 can access additional funding with valid prescription
 *    evidence. Home Modifications High tier is a hard $15,390 per 12-month
 *    period (extendable — see note 3).
 * 2. The Home Modifications High Tier may only be accessed once per lifetime.
 * 3. The short-term Home Modifications budget is available for 12 months (extendable
 *    to 24 months for complex modifications).
 *
 * Apply the values below as the BASE budget; the application layer must enforce
 * the lifetime/duration rules separately. Re-pin on the next 1 July indexation.
 */
export const SAH_ATHM_BUDGET_CAPS = {
  low: 513, // Tier 1: low-cost, low-risk items
  medium: 2_052, // Tier 2: specialised AT or minor modifications
  high: 15_390, // Tier 3: high-cost AT or major modifications (AT can exceed with evidence; HM cannot)
} as const;

/** @deprecated Use SAH_ATHM_BUDGET_CAPS.high instead */
export const SAH_ATHM_BUDGET_CAP = 15_390;

/**
 * SAH care management deduction — HARD CAP at 10% of each quarterly budget.
 *
 * Source: health.gov.au + sensiblecare.com.au + bcd.org.au confirm 10% across all
 * 2025-26 publications. Aged Care Rules 2025 Chapter 4.
 */
export const SAH_CARE_MANAGEMENT_RATE = 0.1;

// ═══════════════════════════════════════════════════════════════════════
// Service-to-tier mapping
// ═══════════════════════════════════════════════════════════════════════

/** Maps granular service types to SAH contribution tiers */
export const SERVICE_TO_SAH_TIER: Record<string, string> = {
  // Clinical tier — 0% consumer contribution
  NURSING: 'CLINICAL',
  ALLIED_HEALTH: 'CLINICAL',
  PODIATRY: 'CLINICAL',
  PHYSIOTHERAPY: 'CLINICAL',
  OCCUPATIONAL_THERAPY: 'CLINICAL',
  SPEECH_PATHOLOGY: 'CLINICAL',
  DIETETICS: 'CLINICAL',
  PSYCHOLOGY: 'CLINICAL',

  // Independence tier — 5-50% depending on pension status
  PERSONAL_CARE: 'INDEPENDENCE',
  ASSISTIVE_TECHNOLOGY: 'INDEPENDENCE',
  HOME_MODIFICATIONS: 'INDEPENDENCE',
  EQUIPMENT: 'INDEPENDENCE',
  SOCIAL_SUPPORT: 'INDEPENDENCE',
  TRANSPORT: 'INDEPENDENCE',

  // Everyday Living tier — 17.5-80% depending on pension status
  DOMESTIC_ASSISTANCE: 'EVERYDAY_LIVING',
  MEALS: 'EVERYDAY_LIVING',
  GARDENING: 'EVERYDAY_LIVING',
  RESPITE: 'EVERYDAY_LIVING',

  // Care management is separately deducted, not income-tested
  CARE_MANAGEMENT: 'CARE_MANAGEMENT',
};
